import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { AlertImageControl } from "@/components/AlertImageControl";
import { MessageControl } from "@/components/MessageControl";
import { AlertControl } from "@/components/AlertControl";
import { LogoControl } from "@/components/logo/LogoControl";
import { Message } from "@/types/ticker";
import { TickerSettings } from "@/components/TickerSettings";

interface ControlPanelProps {
  kitId: string;
  activeKit?: string | null;
  messages?: Array<Message & { is_active: boolean }>;
  allMessages?: Message[];
  alerts?: Array<Message & { is_active: boolean }>;
  markets?: Array<{ short_name: string; name: string }>;
  kitMarket?: { market_short_name: string } | null;
  speed?: number;
  backgroundColor?: string;
  textColor?: string;
  onSpeedChange?: (value: number) => void;
  onBackgroundColorChange?: (color: string) => void;
  onTextColorChange?: (color: string) => void;
  onAddMessage?: (text: string) => void;
  onRemoveMessage?: (id: string) => void;
  onUpdateMessage?: (id: string, text: string) => void;
  onToggleMessageActive?: (id: string, isActive: boolean) => void;
  onAddAlert?: (text: string) => void;
  onRemoveAlert?: (id: string) => void;
  onUpdateAlert?: (id: string, text: string) => void;
  onToggleAlertActive?: (id: string, isActive: boolean) => void;
  onUpdateKitMarket?: (marketShortName: string) => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  kitId,
  activeKit,
  messages = [],
  allMessages = [],
  alerts = [],
  markets = [],
  kitMarket,
  speed = 20,
  backgroundColor = "#000000",
  textColor = "#FFFFFF",
  onSpeedChange = () => {},
  onBackgroundColorChange = () => {},
  onTextColorChange = () => {},
  onAddMessage = () => {},
  onRemoveMessage = () => {},
  onUpdateMessage = () => {},
  onToggleMessageActive = () => {},
  onAddAlert = () => {},
  onRemoveAlert = () => {},
  onUpdateAlert = () => {},
  onToggleAlertActive = () => {},
  onUpdateKitMarket = () => {},
}) => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Ticker Settings</h2>
      <TickerSettings
        speed={speed}
        backgroundColor={backgroundColor}
        textColor={textColor}
        onSpeedChange={onSpeedChange}
        onBackgroundColorChange={onBackgroundColorChange}
        onTextColorChange={onTextColorChange}
      />

      <h2 className="text-xl font-semibold">Logo</h2>
      <LogoControl kitId={kitId} />

      <h2 className="text-xl font-semibold">Alert Images</h2>
      <AlertImageControl kitId={kitId} />

      <h2 className="text-xl font-semibold">Messages</h2>
      <MessageControl 
        messages={messages} 
        allMessages={allMessages}
        onAddMessage={onAddMessage} 
        onRemoveMessage={onRemoveMessage} 
        onUpdateMessage={onUpdateMessage} 
        onToggleActive={onToggleMessageActive} 
      />

      <h2 className="text-xl font-semibold">Alerts</h2>
      <AlertControl 
        alerts={alerts} 
        onAddAlert={onAddAlert} 
        onRemoveAlert={onRemoveAlert} 
        onUpdateAlert={onUpdateAlert} 
        onToggleActive={onToggleAlertActive} 
      />

      {kitId && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Market Selection</h2>
          <Card className="p-4 bg-black/20 border-white/10">
            <Select 
              value={kitMarket?.market_short_name || "SAT"} 
              onValueChange={onUpdateKitMarket}
            >
              <SelectTrigger className="w-full bg-black text-white border-white/20 hover:bg-white/10 transition-colors">
                <SelectValue placeholder="Select market" />
              </SelectTrigger>
              <SelectContent className="bg-black text-white border border-white/20">
                {markets.map(market => (
                  <SelectItem 
                    key={market.short_name} 
                    value={market.short_name} 
                    className="hover:bg-white/10 focus:bg-white/10 cursor-pointer transition-colors"
                  >
                    {market.name} ({market.short_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
        </div>
      )}
    </div>
  );
};
